// External libraries
import { ApolloLink, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error';

// Internal modules
import { $logger } from '@/lib/logger';
import { camelToSnakeCase } from '@/lib/utils';
import { getApiCorrelationId, getDurationBucket } from '../utils';

const NO_ERROR_MESSAGE_AVAILABLE = 'NO_ERROR_MESSAGE_AVAILABLE';

const logDuration = (duration, opName) => {
  const durationBucket = getDurationBucket(duration);

  if (durationBucket) {
    $logger.info(`api_response_time_${opName}_${durationBucket}`);
  }
};

// This link is for logging the result of the API call to CAL and FPTI.
// Those logs are used for debugging, analytics, and populating
// monitoring dashboards.
const link = new ApolloLink((operation, forward) => {
  const operationName = camelToSnakeCase(operation.operationName);
  operation.setContext({ operationStartTime: Date.now() });
  $logger.info(`api_request_${operationName}`);

  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      complete: observer.complete.bind(observer),
      error: observer.error.bind(observer),
      next: response => {
        // Errors from user input will be logged from the `errorLink` that is
        // defined below and concatenated with this link. We need this check
        // because this block of code will be run when there are non-network
        // errors returned in the API response.
        const { errors } = response;
        const hasError = Array.isArray(errors) && Boolean(errors.length);
        if (!hasError) {
          const { operationStartTime } = operation.getContext();
          const duration = Date.now() - operationStartTime;
          logDuration(duration, operationName);
          $logger.info(`api_response_status_200_${operationName}`, {
            apiCorrelationId: getApiCorrelationId({ response }),
            duration,
          });
        }

        observer.next(response);
      },
    });

    return () => subscription?.unsubscribe?.();
  });
});

const errorLink = onError(data => {
  const { graphQLErrors, networkError, operation, response } = data;
  const operationName = camelToSnakeCase(operation.operationName);
  const apiCorrelationId = getApiCorrelationId({ networkError, response });

  // Log errors to CAL and FPTI
  graphQLErrors?.forEach?.(error => {
    const { extensions = {}, message = NO_ERROR_MESSAGE_AVAILABLE } = error;
    const { code = message, exception = {} } = extensions;

    // Logging once per error instead of using a shared payload
    // so that we can make a graph on Grafana, which doesn't have
    // access to the payload.
    $logger.warn(`graphql_error_${operationName}_${code}`, {
      message,
      stack: exception?.stacktrace?.join(' \\n ') || '',
    });

    $logger.fpti('FPTI_API_ERROR', {
      error_source: operationName,
      int_error_code: code,
      int_error_desc: 'gqlContingency',
      state_name: 'billing',
      transition_name: 'process_api_error',
    });
  });

  // Log used to populate Grafana dashboard graph for status codes per
  // operation. `networkError` will not be available for errors resulting
  // from invalid user input or errors like `ACCOUNT_ALREADY_EXISTS`.
  const statusCode = networkError?.statusCode ? '500' : '400';
  $logger.error(`api_error_${statusCode}_${operationName}`, {
    apiCorrelationId,
  });
});

export const loggerLink = link.concat(errorLink);
